import * as THREE from "three";
import type { CowryGeometryParams } from "../core/types";

const LENGTH_SEGMENTS = 192;
const RADIAL_SEGMENTS = 12;
const FIBONACCI_TURNS = 5;
const FIBONACCI_NODES = 13;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export interface SpindleOrgan {
  root: THREE.Group;
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial[]>;
}

export interface SpindleOrganParams {
  thickness: number;
  helixRadius: number;
  exposure: number;
  ribbon: number;
}

export const defaultSpindleOrganParams: SpindleOrganParams = {
  thickness: 0.9,
  helixRadius: 3,
  exposure: 1.5,
  ribbon: 1,
};

export function deriveSpindleOrganParams(itemSeed: string): SpindleOrganParams {
  return {
    ...defaultSpindleOrganParams,
    thickness: 0.8 + hashUnit(`${itemSeed}:spindle-thickness`) * 0.2,
  };
}

export function buildSpindleOrgan(
  params: CowryGeometryParams,
  itemSeed: string,
  foldEmphasis: number,
  spindleParams: SpindleOrganParams = deriveSpindleOrganParams(itemSeed),
): SpindleOrgan {
  validateSpindleParams(spindleParams);
  const root = new THREE.Group();
  root.name = "spindle-organ";

  const materials = createSpindleMaterials();
  const mesh = new THREE.Mesh(
    createSpindleGeometry(params, itemSeed, foldEmphasis, spindleParams),
    materials,
  );
  mesh.name = "spindle-organ-body";
  mesh.castShadow = true;
  root.add(mesh);
  return { root, mesh };
}

function createSpindleMaterials(): THREE.MeshPhysicalMaterial[] {
  const common = {
    side: THREE.DoubleSide,
    clearcoat: 0.68,
    clearcoatRoughness: 0.17,
    opacity: 0.35,
    transparent: true,
    depthWrite: false,
  } as const;
  return [
    new THREE.MeshPhysicalMaterial({
      ...common,
      name: "spindle-outward-metallic",
      color: 0x625778,
      roughness: 0.2,
      metalness: 0.68,
    }),
    new THREE.MeshPhysicalMaterial({
      ...common,
      name: "spindle-inward-dark",
      color: 0x15121d,
      roughness: 0.24,
      metalness: 0.22,
    }),
    new THREE.MeshPhysicalMaterial({
      ...common,
      name: "spindle-contrast-edge",
      color: 0xa8d5d0,
      roughness: 0.16,
      metalness: 0.76,
    }),
  ];
}

function createSpindleGeometry(
  params: CowryGeometryParams,
  itemSeed: string,
  foldEmphasis: number,
  spindleParams: SpindleOrganParams,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const surfaceIndices: [number[], number[], number[]] = [[], [], []];
  const phase = hashUnit(itemSeed) * Math.PI * 2;

  for (let along = 0; along <= LENGTH_SEGMENTS; along += 1) {
    const t = along / LENGTH_SEGMENTS;
    const xUnit = t * 1.72 - 0.86;
    const taperBase = Math.max(0, 1 - Math.pow(Math.abs(xUnit), 2.35));
    const taper = Math.pow(taperBase, params.endTaper);
    const x = xUnit * params.shellLength * 0.5;
    const foldZ = params.ventralFoldCurve * Math.sin(Math.PI * xUnit);
    const surfaceY = ventralCenterY(xUnit, taperBase, taper, params, foldEmphasis);
    const endTaper = Math.pow(Math.sin(t * Math.PI), 0.72);
    const fibonacciPulse = sampleFibonacciPulse(t, phase);
    const radius = params.shellHeight
      * (0.027 + fibonacciPulse * 0.018)
      * endTaper
      * spindleParams.thickness;
    const helixAngle = t * Math.PI * 2 * FIBONACCI_TURNS + phase;
    const helixRadius = params.shellWidth
      * 0.018
      * endTaper
      * spindleParams.helixRadius;
    const centerY = surfaceY
      - radius * (0.34 + spindleParams.exposure)
      + Math.cos(helixAngle) * helixRadius * 0.52;
    const centerZ = foldZ + Math.sin(helixAngle) * helixRadius;

    for (let radial = 0; radial <= RADIAL_SEGMENTS; radial += 1) {
      const angle = radial === RADIAL_SEGMENTS ? 0 : radial / RADIAL_SEGMENTS * Math.PI * 2;
      const ny = Math.cos(angle);
      const nz = Math.sin(angle);
      const verticalRadius = radius * (1 - spindleParams.ribbon * 0.7);
      const lateralRadius = radius * (1 + spindleParams.ribbon * 0.8);
      positions.push(x, centerY + ny * verticalRadius, centerZ + nz * lateralRadius);
      normals.push(0, ny, nz);
      uvs.push(t, radial / RADIAL_SEGMENTS);
    }
  }

  const stride = RADIAL_SEGMENTS + 1;
  for (let along = 0; along < LENGTH_SEGMENTS; along += 1) {
    for (let radial = 0; radial < RADIAL_SEGMENTS; radial += 1) {
      const a = along * stride + radial;
      const b = (along + 1) * stride + radial;
      const angle = (radial + 0.5) / RADIAL_SEGMENTS * Math.PI * 2;
      const verticalNormal = Math.cos(angle);
      const materialIndex = Math.abs(verticalNormal) < 0.36
        ? 2
        : verticalNormal < 0
          ? 0
          : 1;
      surfaceIndices[materialIndex].push(a, b, a + 1, b, b + 1, a + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  const indices = surfaceIndices.flat();
  geometry.setIndex(indices);
  let groupStart = 0;
  surfaceIndices.forEach((surface, materialIndex) => {
    geometry.addGroup(groupStart, surface.length, materialIndex);
    groupStart += surface.length;
  });
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function validateSpindleParams(params: SpindleOrganParams): void {
  if (!Number.isFinite(params.thickness) || params.thickness <= 0) {
    throw new RangeError("Spindle thickness must be finite and positive.");
  }
  if (!Number.isFinite(params.helixRadius) || params.helixRadius < 0) {
    throw new RangeError("Spindle helix radius must be finite and non-negative.");
  }
  if (!Number.isFinite(params.exposure) || params.exposure < -0.5 || params.exposure > 1.5) {
    throw new RangeError("Spindle exposure must be between -0.5 and 1.5.");
  }
  if (!Number.isFinite(params.ribbon) || params.ribbon < 0 || params.ribbon > 1) {
    throw new RangeError("Spindle ribbon amount must be between 0 and 1.");
  }
}

function sampleFibonacciPulse(t: number, phase: number): number {
  let pulse = 0;
  for (let index = 0; index < FIBONACCI_NODES; index += 1) {
    const center = (index + 1) / (FIBONACCI_NODES + 1);
    const phaseWeight = 0.72 + 0.28 * Math.sin(index * GOLDEN_ANGLE + phase);
    const distance = (t - center) / 0.031;
    pulse += Math.exp(-distance * distance) * phaseWeight;
  }
  return Math.min(1, pulse);
}

function ventralCenterY(
  xUnit: number,
  taperBase: number,
  taper: number,
  params: CowryGeometryParams,
  foldEmphasis: number,
): number {
  const endLift = 0.11 * Math.pow(Math.abs(xUnit), 2.4);
  const base = -params.shellHeight * 0.5 * taper * params.ventralFlatness + endLift;
  const foldLift = params.shellHeight * 0.1 * foldEmphasis * Math.pow(taperBase, 0.55);
  return base + foldLift;
}

function hashUnit(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}
